import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Plus, Search, Trash2, X } from 'lucide-react'
import { api, ApiError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

// Debounce dla pola wyszukiwania — search uderza w worker (skan KV), więc nie
// chcemy strzelać przy każdym wciśnięciu klawisza.
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

export default function Redirects() {
  const params = useParams<{ service?: string }>()
  const navigate = useNavigate()
  const services = useQuery({ queryKey: ['services'], queryFn: api.services })

  const activeService = useMemo(() => {
    if (params.service) return params.service
    return services.data?.find((s) => s.enabled)?.code
  }, [params.service, services.data])

  useEffect(() => {
    if (!params.service && activeService) {
      navigate(`/redirects/${activeService}`, { replace: true })
    }
  }, [params.service, activeService, navigate])

  return (
    <div className="flex flex-col gap-4">
      {services.data && activeService && (
        <Tabs value={activeService} onValueChange={(v) => navigate(`/redirects/${v}`)}>
          <TabsList>
            {services.data.map((s) => (
              <TabsTrigger key={s.code} value={s.code} disabled={!s.enabled}>
                {s.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}
      {activeService ? (
        <RedirectManager service={activeService} key={activeService} />
      ) : (
        <p className="text-sm text-muted-foreground">Brak skonfigurowanych serwisów.</p>
      )}
    </div>
  )
}

type Toast = { kind: 'ok' | 'err'; message: string } | null

function RedirectManager({ service }: { service: string }) {
  const queryClient = useQueryClient()
  const [rawQuery, setRawQuery] = useState('')
  const query = useDebounced(rawQuery.trim(), 350)
  const searching = query.length > 0

  const [addOpen, setAddOpen] = useState(false)
  const [toast, setToast] = useState<Toast>(null)

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['redirects', service] })
  }

  // Przeglądanie: natywne listowanie KV stronami (kursor). Ładowane tylko gdy
  // nie ma aktywnego wyszukiwania.
  const browse = useInfiniteQuery({
    queryKey: ['redirects', service, 'browse'],
    queryFn: ({ pageParam }) =>
      api.redirects.list(service, { cursor: pageParam || undefined, limit: 100 }),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.cursor ?? undefined,
    enabled: !searching,
  })

  // Wyszukiwanie po fragmencie — worker skanuje klucze i zwraca dopasowania.
  const search = useQuery({
    queryKey: ['redirects', service, 'search', query],
    queryFn: () => api.redirects.search(service, query),
    enabled: searching,
  })

  const browseSlugs = useMemo(
    () => browse.data?.pages.flatMap((p) => p.slugs) ?? [],
    [browse.data],
  )
  const slugs = searching ? search.data?.matches ?? [] : browseSlugs

  return (
    <div className="flex flex-col gap-4">
      {toast && (
        <div
          className={
            'rounded-md border px-3 py-2 text-sm ' +
            (toast.kind === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : 'border-destructive/30 bg-destructive/10 text-destructive')
          }
        >
          {toast.message}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[260px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
            placeholder="Szukaj po fragmencie sluga, np. samsung-galaxy…"
            className="px-8"
          />
          {rawQuery && (
            <button
              type="button"
              onClick={() => setRawQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="size-4" /> Dodaj przekierowanie
        </Button>
      </div>

      <div className="text-xs text-muted-foreground">
        {searching ? (
          search.isFetching ? (
            'Szukam…'
          ) : search.data ? (
            <>
              Znaleziono {search.data.matches.length}
              {search.data.truncated && '+'} dopasowań (przeskanowano{' '}
              {search.data.scanned.toLocaleString('pl-PL')} kluczy).
              {search.data.truncated && ' Zawęź zapytanie, by zobaczyć resztę.'}
            </>
          ) : null
        ) : (
          <>Wczytano {browseSlugs.length.toLocaleString('pl-PL')} slugów.</>
        )}
      </div>

      {(searching ? search.isError : browse.isError) && (
        <p className="text-sm text-destructive">
          Błąd: {String(searching ? search.error : browse.error)}
        </p>
      )}

      <Card className="py-0">
        <CardContent className="px-0">
          <ul className="divide-y">
            {slugs.length === 0 && !browse.isLoading && !search.isFetching && (
              <li className="px-4 py-6 text-center text-sm text-muted-foreground">
                {searching ? 'Brak dopasowań.' : 'Brak przekierowań.'}
              </li>
            )}
            {slugs.map((slug) => (
              <RedirectRow
                key={slug}
                service={service}
                slug={slug}
                onChanged={invalidate}
                onToast={setToast}
              />
            ))}
          </ul>
        </CardContent>
      </Card>

      {!searching && browse.hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => browse.fetchNextPage()}
            disabled={browse.isFetchingNextPage}
          >
            {browse.isFetchingNextPage ? 'Ładowanie…' : 'Załaduj więcej'}
          </Button>
        </div>
      )}

      {addOpen && (
        <RedirectFormModal
          service={service}
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            invalidate()
            setAddOpen(false)
            setToast({ kind: 'ok', message: 'Dodano przekierowanie.' })
          }}
        />
      )}
    </div>
  )
}

function RedirectRow({
  service,
  slug,
  onChanged,
  onToast,
}: {
  service: string
  slug: string
  onChanged: () => void
  onToast: (t: Toast) => void
}) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(false)

  const value = useQuery({
    queryKey: ['redirect-value', service, slug],
    queryFn: () => api.redirects.get(service, slug),
    enabled: open,
  })

  const remove = useMutation({
    mutationFn: () => api.redirects.remove(service, slug),
    onSuccess: () => {
      onToast({ kind: 'ok', message: `Usunięto „${slug}”.` })
      onChanged()
    },
    onError: (e) => onToast({ kind: 'err', message: (e as Error).message }),
  })

  return (
    <li className="text-sm">
      <div className="flex items-center gap-3 px-4 py-2.5">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex flex-1 items-center gap-2 truncate text-left font-mono text-xs hover:text-foreground"
          title={slug}
        >
          {open ? (
            <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
          )}
          <span className="truncate">{slug}</span>
        </button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setOpen(true)
            setEditing(true)
          }}
        >
          Edytuj
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-destructive hover:text-destructive"
          disabled={remove.isPending}
          onClick={() => {
            if (confirm(`Usunąć przekierowanie „${slug}”?`)) remove.mutate()
          }}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>

      {open && (
        <div className="border-t bg-muted/40 px-4 py-3">
          {value.isLoading && <p className="text-xs text-muted-foreground">Ładowanie wartości…</p>}
          {value.isError && (
            <p className="text-xs text-destructive">Błąd odczytu: {String(value.error)}</p>
          )}
          {value.data && !editing && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">→</span>
              <span className="font-mono text-xs break-all">{value.data.target}</span>
            </div>
          )}
          {value.data && editing && (
            <RedirectInlineEdit
              service={service}
              slug={slug}
              initialTarget={value.data.target}
              onCancel={() => setEditing(false)}
              onSaved={(renamed) => {
                setEditing(false)
                queryClient.invalidateQueries({ queryKey: ['redirect-value', service, slug] })
                onToast({ kind: 'ok', message: 'Zapisano zmianę.' })
                if (renamed) onChanged()
              }}
            />
          )}
        </div>
      )}
    </li>
  )
}

function RedirectInlineEdit({
  service,
  slug,
  initialTarget,
  onCancel,
  onSaved,
}: {
  service: string
  slug: string
  initialTarget: string
  onCancel: () => void
  onSaved: (renamed: boolean) => void
}) {
  const [newSlug, setNewSlug] = useState(slug)
  const [target, setTarget] = useState(initialTarget)
  const [err, setErr] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: async () => {
      const trimmedSlug = newSlug.trim()
      const trimmedTarget = target.trim()
      if (!trimmedSlug || !trimmedTarget) throw new Error('Slug i URL nie mogą być puste.')
      if (trimmedSlug === slug) {
        await api.redirects.update(service, slug, trimmedTarget)
        return false
      }
      // Zmiana sluga = nowy klucz + usunięcie starego. Najpierw sprawdzamy kolizję.
      await api.redirects.create(service, trimmedSlug, trimmedTarget)
      await api.redirects.remove(service, slug)
      return true
    },
    onSuccess: (renamed) => onSaved(renamed),
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        setErr('Slug docelowy już istnieje — wybierz inny.')
        return
      }
      setErr((e as Error).message)
    },
  })

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <Label className="text-xs">Slug</Label>
        <Input
          value={newSlug}
          onChange={(e) => setNewSlug(e.target.value)}
          className="font-mono text-xs"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">URL docelowy / kod</Label>
        <Input
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="font-mono text-xs"
        />
      </div>
      {err && <p className="text-xs text-destructive">{err}</p>}
      <div className="flex gap-2">
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Zapisywanie…' : 'Zapisz'}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Anuluj
        </Button>
      </div>
    </div>
  )
}

function RedirectFormModal({
  service,
  onClose,
  onSaved,
}: {
  service: string
  onClose: () => void
  onSaved: () => void
}) {
  const [slug, setSlug] = useState('')
  const [target, setTarget] = useState('')
  const [err, setErr] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: async () => {
      const s = slug.trim()
      const t = target.trim()
      if (!s || !t) throw new Error('Slug i URL nie mogą być puste.')
      return api.redirects.create(service, s, t)
    },
    onSuccess: onSaved,
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        setErr('Ten slug już istnieje. Użyj edycji albo wybierz inny.')
        return
      }
      if (e instanceof ApiError && e.status === 422) {
        setErr('Walidacja nieudana — sprawdź dane.')
        return
      }
      setErr((e as Error).message)
    },
  })

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Nowe przekierowanie ({service})</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="r-slug">Slug</Label>
            <Input
              id="r-slug"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value)
                if (err) setErr(null)
              }}
              placeholder="/2025/07/samsung-galaxy-z-fold-7.html"
              className="font-mono text-xs"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="r-target">URL docelowy / kod statusu</Label>
            <Input
              id="r-target"
              value={target}
              onChange={(e) => {
                setTarget(e.target.value)
                if (err) setErr(null)
              }}
              placeholder="https://spidersweb.pl/"
              className="font-mono text-xs"
            />
          </div>
          {err && <p className="text-xs text-destructive">{err}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Zapisywanie…' : 'Dodaj'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
